import { LenderName, Product, Program } from '@nemuru-eng/js-api-client';

import {
  CredentialFieldConfig,
  CredentialLenderNotice,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialFieldConfig';

const configKey = (lender: LenderName, product: Product, program: Program): string => `${lender}|${product}|${program}`;

const credentialFieldConfigMap = new Map<string, CredentialFieldConfig[]>();
const credentialLenderNoticeMap = new Map<string, CredentialLenderNotice>();

const register = (
  lender: LenderName,
  products: Product[],
  programs: Program[],
  fields: CredentialFieldConfig[],
  lenderNotice?: CredentialLenderNotice,
) => {
  for (const product of products) {
    for (const program of programs) {
      credentialFieldConfigMap.set(configKey(lender, product, program), fields);
      if (lenderNotice) {
        credentialLenderNoticeMap.set(configKey(lender, product, program), lenderNotice);
      }
    }
  }
};

// ─── Abanca ──────────────────────────────────────────────────────────────────

register(
  LenderName.ABANCA,
  [Product.AUTO],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'username', label: 'Código de usuario', required: true, placeholder: 'WS850908' },
    { platformField: 'password', label: 'Contraseña', required: true, placeholder: 'abanca_secret', sensitive: true },
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de comercio',
      required: true,
      placeholder: '850908',
    },
    { platformField: 'channelIdInLenderContext', label: 'Canal', required: false, placeholder: 'canal_001' },
    { platformField: 'agentIdInLenderContext', label: 'Agente', required: false, placeholder: 'agente_001' },
  ],
);

register(
  LenderName.ABANCA,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de establecimiento',
      required: true,
      placeholder: '850908',
    },
    { platformField: 'username', label: 'Usuario WS', required: true, placeholder: 'WS850908' },
    {
      platformField: 'password',
      label: 'Contraseña WS',
      required: true,
      placeholder: '58902',
      sensitive: true,
    },
    { platformField: 'channelIdInLenderContext', label: 'Canal Abanca', required: false, placeholder: 'canal_001' },
    { platformField: 'agentIdInLenderContext', label: 'Agente Abanca', required: false, placeholder: '850908' },
  ],
);

// ─── Aplazame ─────────────────────────────────────────────────────────────────

register(
  LenderName.APLAZAME,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Public Key',
      required: true,
      placeholder: 'd506139ac83c5b748143e925ad083e22d1bfaf7a',
    },
    {
      platformField: 'password',
      label: 'Private Key',
      required: true,
      placeholder: 'aplazame_private_key',
      sensitive: true,
    },
  ],
);

// ─── Banc Sabadell ────────────────────────────────────────────────────────────

register(
  LenderName.BS,
  [Product.AUTO],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'merchantIdInLenderContext', label: 'Código acuerdo', required: true, placeholder: '64322' },
    {
      platformField: 'channelIdInLenderContext',
      label: 'Código Establecimiento',
      required: true,
      placeholder: '64322001',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'Código Vendedor',
      required: true,
      placeholder: '64322001001',
    },
    { platformField: 'username', label: 'Código WS', required: true, placeholder: 'ws64320' },
    { platformField: 'password', label: 'Contraseña', required: true, placeholder: 'nuoy4j7BTKU=', sensitive: true },
  ],
);

register(
  LenderName.BS,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'merchantIdInLenderContext', label: 'Código acuerdo', required: true, placeholder: '64322' },
    {
      platformField: 'channelIdInLenderContext',
      label: 'Código Establecimiento',
      required: true,
      placeholder: '64322001',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'Código Vendedor',
      required: false,
      placeholder: '64322001001',
    },
    { platformField: 'username', label: 'Código WS', required: true, placeholder: 'ws64320' },
    { platformField: 'password', label: 'Contraseña', required: true, placeholder: 'nuoy4j7BTKU=', sensitive: true },
  ],
);

// ─── BBVA ─────────────────────────────────────────────────────────────────────

register(
  LenderName.BBVA,
  [Product.AUTO],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'ID Colaborador ## Establecimiento',
      required: true,
      placeholder: '01828610047300000000247199000000000##EBRO_GIRONA',
    },
    {
      platformField: 'channelIdInLenderContext',
      label: 'Nombre Canal',
      required: false,
      placeholder: 'EBRO_GIRONA',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'ID Vendedor ## Vendedor',
      required: true,
      placeholder: '01828610047500000000326494000000000##XAVIER_CARRERAS',
    },
    { platformField: 'clientId', label: 'Client ID', required: true, placeholder: '175040346620' },
    {
      platformField: 'clientSecret',
      label: 'Client Secret',
      required: true,
      placeholder: '52b250a0ae7b496f9bf1a6fdbcf255e3',
      sensitive: true,
    },
  ],
);

register(
  LenderName.BBVA,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'merchantIdInLenderContext', label: 'Usuario', required: true, placeholder: '0864512' },
    { platformField: 'password', label: 'Clave', required: true, placeholder: '12736128', sensitive: true },
  ],
);

// ─── CaixaBank ────────────────────────────────────────────────────────────────

register(
  LenderName.CAIXABANK,
  [Product.AUTO],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de Comercio',
      required: true,
      placeholder: '60732004',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'Código Vendedor CPC',
      required: false,
      placeholder: '6073200400006',
    },
  ],
);

register(
  LenderName.CAIXABANK,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de Comercio',
      required: true,
      placeholder: '58997003',
    },
  ],
);

credentialFieldConfigMap.set(configKey(LenderName.CAIXABANK, Product.CARD, Program.NO_PURCHASE), [
  {
    platformField: 'merchantIdInLenderContext',
    label: 'Código de Comercio',
    required: true,
    placeholder: '66171014',
  },
]);

register(
  LenderName.CAIXABANK,
  [Product.CREDIT_LINE],
  [Program.NO_PURCHASE, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de Comercio',
      required: true,
      placeholder: '66171002',
    },
  ],
);

// ─── Cetelem ──────────────────────────────────────────────────────────────────

const CETELEM_SELLER_PLACEHOLDER = '2468239';
const CETELEM_ISSUER_PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

register(
  LenderName.CETELEM,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'SELLER / VENDORID',
      required: true,
      placeholder: CETELEM_SELLER_PLACEHOLDER,
      tooltip: 'Código de vendedor que da Cetelem: el vendorId de Subscriptions y el seller del JWT.',
    },
    {
      platformField: 'clientId',
      label: 'ISSUER JWT (Client ID)',
      required: true,
      placeholder: CETELEM_ISSUER_PLACEHOLDER,
      tooltip:
        'Client ID de la aplicación en Cetelem: es el emisor (iss) del JWT. Cetelem da uno por canal (online o tiendas).',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'Vendedor (salesAgentId)',
      required: false,
      placeholder: 'Nombre del vendedor',
      tooltip: 'Se envía a Subscriptions como salesAgentId. Si se deja vacío, se usa el nombre del agente.',
    },
    {
      platformField: 'username',
      label: 'Usuario (cliente conocido)',
      required: false,
      placeholder: '000000',
      tooltip: 'Solo para la consulta de cliente conocido (API Customers). Sin él, no se consulta.',
    },
    {
      platformField: 'password',
      label: 'Contraseña (cliente conocido)',
      required: false,
      sensitive: true,
      tooltip: 'Solo para la consulta de cliente conocido (API Customers).',
    },
    {
      platformField: 'clientSecret',
      label: 'Client secret (cliente conocido)',
      required: false,
      sensitive: true,
      tooltip: 'Secreto del Client ID, solo para la consulta de cliente conocido (API Customers).',
    },
  ],
  {
    notice:
      'Credencial de la API Subscriptions de Cetelem (alta, estado, documentos y firma) y de la consulta de cliente ' +
      'conocido. El enlace con PF Connect ' +
      '(activación, cancelación y devoluciones) usa la credencial «cetelem_pf_connect» de este mismo agente, con el ' +
      'mismo producto y programa: créala también. Sin ella, la solicitud se da de alta solo en Subscriptions.',
  },
);

register(
  LenderName.CETELEM_PF_CONNECT,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'SELLER / VENDORID',
      required: true,
      placeholder: CETELEM_SELLER_PLACEHOLDER,
      tooltip:
        'Código de vendedor (tienda PF) que da Cetelem. Va en la ruta de PF Connect (vendors/…) y es el seller del JWT.',
    },
    {
      platformField: 'channelIdInLenderContext',
      label: 'PARTNER_ID',
      required: true,
      placeholder: 'BNPPKTUIN',
      tooltip: 'Identificador de la relación comercial con Cetelem. Va en la ruta de todas las llamadas (partners/…).',
    },
    {
      platformField: 'agentIdInLenderContext',
      label: 'PARTNERSTOREID',
      required: false,
      placeholder: 'KTUINONLINE',
      tooltip:
        'Identificador de vuestra tienda o canal (p. ej. KTUINONLINE para online). Si se deja vacío, se usa el PARTNER_ID.',
    },
    {
      platformField: 'clientId',
      label: 'ISSUER JWT (Client ID)',
      required: true,
      placeholder: CETELEM_ISSUER_PLACEHOLDER,
      tooltip: 'Emisor (iss) con el que firmamos el JWT de PF Connect. Cetelem da uno por canal (online o tiendas).',
    },
  ],
  {
    notice:
      'Credencial de la API PF Connect de Cetelem. La usa el préstamo «cetelem_pf_connect» y también el enlace PF ' +
      'de las solicitudes «cetelem» de este agente (mismo producto y programa). Copia los valores tal como los envía ' +
      'Cetelem: SELLER, PARTNER_ID, PARTNERSTOREID e ISSUER JWT.',
    disableChannelAndAgentAutofill: true,
  },
);

register(
  LenderName.CETELEM,
  [Product.AUTO],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'merchantIdInLenderContext', label: 'Seller', required: true, placeholder: '8614972' },
    { platformField: 'username', label: 'Username', required: true, placeholder: '557151' },
    { platformField: 'password', label: 'Password', required: true, placeholder: '87097957', sensitive: true },
    {
      platformField: 'clientId',
      label: 'ClientID (consumer-key)',
      required: true,
      placeholder: '12ae8059-612f-49a0-9349-c220c81f5460',
    },
    {
      platformField: 'clientSecret',
      label: 'Secret key (consumer-secret)',
      required: true,
      placeholder: 'c615424d-89c2-449f-b4ce-1614ee48db4a',
      sensitive: true,
    },
  ],
);

// ─── Cofidis ──────────────────────────────────────────────────────────────────

register(
  LenderName.COFIDIS,
  [Product.AUTO, Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código de comercio',
      required: true,
      placeholder: '7002597',
    },
  ],
);

// ─── FINONLINE ─────────────────────────────────────────────────────────────────────

register(
  LenderName.FINONLINE,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código establecimiento',
      required: true,
      placeholder: '20843009',
    },
    {
      platformField: 'clientId',
      label: 'Client ID OAuth2',
      required: true,
      placeholder: 'd1f62d59-dfa0-4b58-a639-5c4ea372b5fg',
    },
    {
      platformField: 'clientSecret',
      label: 'Client Secret OAuth2',
      required: true,
      placeholder: 'cc74beba-9adc-4145-93db-49e7b3192aa0',
      sensitive: true,
    },
  ],
);

// ─── FLOA ─────────────────────────────────────────────────────────────────────

register(
  LenderName.FLOA,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'MerchantID (code_marchand)',
      required: true,
      placeholder: '3436',
    },
    {
      platformField: 'username',
      label: 'identifiant',
      required: true,
      placeholder: 'jx^LIBERIAESuoZUD~gvB^...',
    },
    {
      platformField: 'password',
      label: 'mot_de_passe',
      required: true,
      placeholder: 'n6$HUMbdjiSKXJFZNpb^3yR1r...',
      sensitive: true,
    },
    { platformField: 'agentIdInLenderContext', label: 'AGENCYNAME', required: true, placeholder: 'AGENCIA_NOMBRE' },
  ],
);

// ─── Nemuru ───────────────────────────────────────────────────────────────────

register(
  LenderName.NEMURU,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Merchant ID',
      required: true,
      placeholder: '372fdbee-a63d-4726-b127-f3531499a6f4',
    },
  ],
);

// ─── Pepper ───────────────────────────────────────────────────────────────────

register(
  LenderName.PEPPER,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Usuario',
      required: true,
      placeholder: 'NMR75080',
    },
    // { platformField: 'username', label: 'NMR Merchant Code (username)', required: false, placeholder: 'NMR75080' },
    {
      platformField: 'password',
      label: 'Password',
      required: true,
      placeholder: 'TQOU4]HUDGS]U8OIDA5?',
      sensitive: true,
    },
  ],
);

// ─── Santander Consumer ───────────────────────────────────────────────────────

credentialFieldConfigMap.set(configKey(LenderName.SANTANDER_CONSUMER, Product.AUTO, Program.FREE_PROGRAM), [
  { platformField: 'username', label: 'CodigoUsuario', required: true, placeholder: 'NEMURU' },
  {
    platformField: 'password',
    label: 'Contraseña SOAP',
    required: true,
    placeholder: 'NE_1MU_2RU_3¡',
    sensitive: true,
  },
  { platformField: 'merchantIdInLenderContext', label: 'Originator ID', required: true, placeholder: '1056195' },
  { platformField: 'channelIdInLenderContext', label: 'DealerCode', required: true, placeholder: '163072' },
  { platformField: 'agentIdInLenderContext', label: 'SALESMANID', required: false, placeholder: '163072' },
]);

register(
  LenderName.SANTANDER_CONSUMER,
  [Product.CARD],
  [Program.NO_PURCHASE, Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'username', label: 'CodigoUsuario', required: true, placeholder: 'NEMURU' },
    {
      platformField: 'password',
      label: 'Contraseña SOAP',
      required: true,
      placeholder: 'NE_1MU_2RU_3¡',
      sensitive: true,
    },
    { platformField: 'merchantIdInLenderContext', label: 'Código POS', required: true, placeholder: '1056195' },
    { platformField: 'channelIdInLenderContext', label: 'Canal', required: false, placeholder: '163072' },
    { platformField: 'agentIdInLenderContext', label: 'Agente', required: false, placeholder: '163072' },
  ],
);

// ─── Santander (deprecated) ───────────────────────────────────────────────────

register(
  LenderName.SANTANDER,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    { platformField: 'username', label: 'CodigoUsuario', required: true, placeholder: 'NEMURU' },
    {
      platformField: 'password',
      label: 'Contraseña SOAP',
      required: true,
      placeholder: 'san_soap_secret',
      sensitive: true,
    },
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Código Establecimiento',
      required: true,
      placeholder: 'EST_00123',
    },
  ],
);

// ─── SeQura ───────────────────────────────────────────────────────────────────

register(
  LenderName.SEQURA,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'Merchant Id',
      required: true,
      placeholder: 'superwagen_tc',
    },
    {
      platformField: 'password',
      label: 'Clave assets',
      required: true,
      placeholder: 'uG7IuX4IOc',
      sensitive: true,
    },
    {
      platformField: 'channelIdInLenderContext',
      label: 'Store Ref',
      required: false,
      placeholder: 'CLINICA CONSUEGRA',
    },
    { platformField: 'agentIdInLenderContext', label: 'Operator Ref', required: false, placeholder: 'ELENA' },
  ],
);

// ─── Sofinco (Soyou) ──────────────────────────────────────────────────────────

register(
  LenderName.SOYOU,
  [Product.CONSUMER_LOAN],
  [Program.FREE_PROGRAM, Program.ZERO_PROGRAM],
  [
    {
      platformField: 'merchantIdInLenderContext',
      label: 'ID actor',
      required: true,
      placeholder: 'A01369796',
    },
    {
      platformField: 'password',
      label: 'Contraseña',
      required: true,
      placeholder: 'Barcelna2125!',
      sensitive: true,
    },
  ],
);

// ─── SeQura Quix ──────────────────────────────────────────────────────────────

credentialFieldConfigMap.set(configKey(LenderName.SEQURA_QUIX, Product.CONSUMER_LOAN, Program.FREE_PROGRAM), [
  {
    platformField: 'merchantIdInLenderContext',
    label: 'Merchant Id',
    required: true,
    placeholder: 'rossellimac_tfc',
  },
  {
    platformField: 'password',
    label: 'Clave assets',
    required: true,
    placeholder: 'uG7IuX4IOc',
    sensitive: true,
  },
  {
    platformField: 'channelIdInLenderContext',
    label: 'Store Ref',
    required: false,
    placeholder: 'Rossellimac CC Zielo Shopping',
  },
  {
    platformField: 'agentIdInLenderContext',
    label: 'Operator Ref',
    required: false,
    placeholder: 'Rossellimac CC Zielo Shopping',
  },
]);

// ─── Lookup ───────────────────────────────────────────────────────────────────

export const getCredentialLenderNotice = (
  lender: LenderName,
  product: Product,
  program: Program,
): CredentialLenderNotice | undefined => credentialLenderNoticeMap.get(configKey(lender, product, program));

export const getCredentialFieldsConfig = (
  lenderName: LenderName,
  product: Product,
  program: Program,
): CredentialFieldConfig[] | undefined => credentialFieldConfigMap.get(configKey(lenderName, product, program));
